import React, { useMemo, useState } from "react";
import { Drawer, AppBar, Toolbar, IconButton, Box, Stack, Typography, Button, Avatar, useMediaQuery } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { useAppContext } from "../context/MainContext/AppContext";
import { assignerLinks, refereeLinks } from "../utils/LinkData";
import useRouter from "../hooks/router";
import { Role } from "../context/MainContext/Constants";
import { getPhotoFromDbUser, handleImageError } from "../utils/Utils";

const navItems = (role) => (role === Role.Assigner ? assignerLinks : refereeLinks);

const NavButton = ({ label, active, onClick }) => (
  <Button
    onClick={onClick}
    variant={active ? "contained" : "text"}
    sx={{
      borderRadius: 999,
      px: 2,
      py: 1,
      textTransform: "none",
      fontWeight: 700,
      color: active ? "#0d2230" : "#e8f0f4",
      bgcolor: active ? "#00c9a7" : "transparent",
      '&:hover': { bgcolor: active ? '#00c9a7' : 'rgba(255,255,255,0.08)' },
    }}
  >
    {label}
  </Button>
);

export default function ModernShell({ title, subtitle, children, actions, noShell = false }) {
  const { role, database_user } = useAppContext();
  const { handleNavigation } = useRouter();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);

  const items = useMemo(() => navItems(role), [role]);

  if (noShell) return <>{children}</>;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0f1e29', color: '#e8f0f4' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#1a3a4a', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Toolbar sx={{ minHeight: 72, px: { xs: 2, md: 3 }, display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            {mobile && (
              <IconButton onClick={() => setOpen(true)} sx={{ color: '#e8f0f4' }}>
                <MenuIcon />
              </IconButton>
            )}
            <img src="/logo.svg" alt="RefAlign" style={{ height: 34, cursor: 'pointer' }} onClick={() => handleNavigation(role === Role.Assigner ? '/assigner/' : '/referee/')} />
            {!mobile && (
              <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                {items.map((item) => (
                  <NavButton key={item.name} label={item.name} onClick={() => handleNavigation(item.path)} />
                ))}
              </Stack>
            )}
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {!mobile && title && <Typography variant="h6" sx={{ color: '#e8f0f4', fontWeight: 800 }}>{title}</Typography>}
            {database_user && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 1 }}>
                <Avatar src={getPhotoFromDbUser(database_user)} onError={handleImageError} sx={{ width: 36, height: 36 }} />
                {!mobile && <Typography variant="body2" sx={{ fontWeight: 700 }}>{database_user.Name}</Typography>}
              </Stack>
            )}
          </Stack>
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { bgcolor: '#111e27', color: '#e8f0f4', width: 300 } }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 800 }}>Menu</Typography>
          <IconButton onClick={() => setOpen(false)} sx={{ color: '#e8f0f4' }}><CloseIcon /></IconButton>
        </Box>
        <Stack sx={{ px: 2, pb: 2 }} spacing={1}>
          {items.map((item) => <Button key={item.name} onClick={() => { setOpen(false); handleNavigation(item.path); }} sx={{ justifyContent: 'flex-start', color: '#e8f0f4', textTransform: 'none', borderRadius: 3, py: 1.25, bgcolor: 'rgba(255,255,255,0.04)' }}>{item.name}</Button>)}
        </Stack>
      </Drawer>
      <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2, md: 3 } }}>
        {(title || subtitle || actions) && (
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Box>
              {title && <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: -0.5 }}>{title}</Typography>}
              {subtitle && <Typography variant="body1" sx={{ color: 'rgba(232,240,244,0.72)', mt: 0.5 }}>{subtitle}</Typography>}
            </Box>
            {actions}
          </Stack>
        )}
        {children}
      </Box>
    </Box>
  );
}
